import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ClipboardCheck, Download, FileText, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import PostGenScheduleDialog from "./PostGenScheduleDialog";

// Strands map to expectation-code prefixes per Ontario math 2020.
// Fallback only — the live list is fetched from /api/curriculum so there
// is one source. If the call is slow or fails, this keeps the dropdown
// populated (an empty curriculum picker is worse than a stale label).
const STRANDS_FALLBACK = [
  { value: "Number", letter: "B" },
  { value: "Algebra", letter: "C" },
  { value: "Data", letter: "D" },
  { value: "Spatial Sense", letter: "E" },
  { value: "Financial Literacy", letter: "F" },
];

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

const DIFFICULTY_OPTIONS = [
  { value: "intervention", label: "Intervention (below grade)" },
  { value: "on_grade", label: "On grade" },
  { value: "extension", label: "Extension (above grade)" },
];

const LAYOUT_OPTIONS = [
  { value: "one_column", label: "1 column" },
  { value: "two_column", label: "2 columns" },
  { value: "grid", label: "Grid (rote drills)" },
];

const QUESTION_COUNTS = [5, 10, 15, 20];

const POLL_MS = 3000;

const Section = ({ label, hint, children, testid }) => (
  <div className="mb-5" data-testid={testid}>
    <Label className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984] mb-2 block">
      {label}
    </Label>
    {hint && (
      <p className="text-xs text-[#4a6984]/65 mb-2 font-medium">{hint}</p>
    )}
    {children}
  </div>
);

const Pill = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition border ${
      active
        ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
        : "bg-white text-[#4a6984] border-[#4a6984]/20 hover:bg-[#f4f5f7]"
    }`}
  >
    {children}
  </button>
);

// ============================================================
// Worksheet Builder — top-level panel rendered inside MathEngine
// ============================================================
export default function WorksheetBuilder({ mode: initialMode = "custom_build", onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [recLoading, setRecLoading] = useState(initialMode === "close_the_gap");
  const [recommendation, setRecommendation] = useState(null);

  // Form state — pre-populated by recommendation when in close_the_gap mode.
  const [grade, setGrade] = useState("Grade 5");
  const [strand, setStrand] = useState("Number");
  const [strands, setStrands] = useState(STRANDS_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/curriculum");
        const byGrade = data?.strands || {};
        let mathStrands = null;
        for (const g of Object.keys(byGrade)) {
          const m = byGrade[g]?.["Mathematics"];
          if (Array.isArray(m) && m.length) { mathStrands = m; break; }
        }
        const mapped = (mathStrands || [])
          .filter((s) => ["B", "C", "D", "E", "F"].includes(s.code))
          .map((s) => ({ value: s.strand_name || s.name, letter: s.code }));
        if (!cancelled && mapped.length) setStrands(mapped);
      } catch (e) {
        /* keep STRANDS_FALLBACK — never render an empty strand picker */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [expectations, setExpectations] = useState([]); // available expectations for grade+strand
  const [expectationsLoading, setExpectationsLoading] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [questionTypes, setQuestionTypes] = useState(["mixed"]);
  const [difficulty, setDifficulty] = useState("on_grade");
  const [questionCount, setQuestionCount] = useState(10);
  const [layout, setLayout] = useState("one_column");
  const [title, setTitle] = useState("");
  const [generateTrio, setGenerateTrio] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]); // [{ job_id, difficulty, status, activity }]
  // Phase 88 — most recently-completed worksheet to drive the post-gen
  // Planner popup. Mirrors the Generator's behaviour.
  const [justGenerated, setJustGenerated] = useState(null);

  // Load Close-the-Gap recommendation on mount when applicable.
  useEffect(() => {
    if (initialMode !== "close_the_gap") return;
    let abort = false;
    (async () => {
      try {
        const { data } = await api.get("/math-engine/worksheet/recommend");
        if (abort) return;
        setRecommendation(data);
        if (data?.available && data.suggestion) {
          const s = data.suggestion;
          setGrade(s.grade || "Grade 5");
          setStrand(s.strand || "Number");
          setSelectedCodes(s.expectation_codes || []);
          setQuestionTypes(s.question_types || ["mixed"]);
          setDifficulty(s.difficulty || "on_grade");
          setQuestionCount(s.question_count || 10);
          setLayout(s.layout || "one_column");
          setTitle(s.title || "");
        }
      } catch {
        toast.error("Couldn't load recommendation");
      } finally {
        if (!abort) setRecLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [initialMode]);

  // Fetch expectations whenever grade/strand changes.
  useEffect(() => {
    let abort = false;
    setExpectationsLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/math-engine/worksheet/expectations", {
          params: { grade, strand },
        });
        if (!abort) setExpectations(data.expectations || []);
      } catch {
        if (!abort) setExpectations([]);
      } finally {
        if (!abort) setExpectationsLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [grade, strand]);

  // Available question types — fetched once from server so the catalogue stays in sync.
  const [qTypeCatalogue, setQTypeCatalogue] = useState([
    { key: "mixed", label: "Mixed (recommended)" },
    { key: "rote_practice", label: "Rote practice" },
    { key: "word_problems", label: "Word problems" },
    { key: "visual_problems", label: "Visual problems" },
    { key: "multi_step", label: "Multi-step problems" },
    { key: "open_ended", label: "Open-ended problems" },
    { key: "diagnostic", label: "Diagnostic questions" },
  ]);
  useEffect(() => {
    api
      .get("/math-engine/worksheet/question-types")
      .then((r) => setQTypeCatalogue(r.data.types || []))
      .catch(() => {});
  }, []);

  const toggleCode = (code) =>
    setSelectedCodes((cur) =>
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
    );

  const toggleQType = (key) =>
    setQuestionTypes((cur) => {
      if (key === "mixed") return ["mixed"];
      const next = cur.filter((c) => c !== "mixed");
      return next.includes(key) ? next.filter((c) => c !== key) : [...next, key];
    });

  const submit = async () => {
    if (selectedCodes.length === 0) {
      toast.error("Pick at least one Ontario expectation");
      return;
    }
    setSubmitting(true);
    setJobs([]);
    try {
      const body = {
        grade,
        strand,
        expectation_codes: selectedCodes,
        question_types: questionTypes,
        difficulty,
        question_count: questionCount,
        layout,
        title,
        mode,
        generate_trio: generateTrio,
        source_activity_ids: recommendation?.suggestion?.source_activity_ids,
      };
      const { data } = await api.post("/math-engine/worksheet/generate", body);
      const initial = (data.jobs || []).map((j) => ({
        job_id: j.job_id,
        difficulty: j.difficulty,
        status: "running",
        activity: null,
        error: null,
        progress: "",
      }));
      setJobs(initial);
      // Poll each job
      initial.forEach((j) => pollJob(j.job_id));
    } catch (e) {
      toast.error("Couldn't start worksheet build", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const pollJob = (jobId) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        // Feb 10 2026 — surface backend waypoints on each job card so
        // the "Building…" line reflects real progress ("Validating
        // question quality", "Retrying after empty response") instead
        // of a static verb. `progress` is only updated when the polled
        // string is non-empty & different from what we last stored.
        if (
          typeof data.progress === "string" &&
          data.progress.trim() &&
          data.status !== "done"
        ) {
          setJobs((cur) =>
            cur.map((j) =>
              j.job_id === jobId && j.progress !== data.progress
                ? { ...j, progress: data.progress }
                : j,
            ),
          );
        }
        if (data.status === "done") {
          setJobs((cur) =>
            cur.map((j) =>
              j.job_id === jobId
                ? { ...j, status: "done", activity: data.activity, progress: "" }
                : j,
            ),
          );
          // Phase 88 — surface the Planner popup the first time this
          // activity transitions to done. If multiple worksheets land
          // back-to-back, the latest one wins.
          if (data.activity?.id) setJustGenerated(data.activity);
          return;
        }
        if (data.status === "error") {
          setJobs((cur) =>
            cur.map((j) =>
              j.job_id === jobId
                ? { ...j, status: "error", error: data.error, progress: "" }
                : j,
            ),
          );
          return;
        }
      } catch {
        /* keep polling */
      }
      if (attempts < 60) setTimeout(tick, POLL_MS); // ~3 min max
    };
    setTimeout(tick, POLL_MS);
  };

  const anyRunning = jobs.some((j) => j.status === "running");
  const anyDone = jobs.some((j) => j.status === "done");

  return (
    <div data-testid="worksheet-builder">
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={onClose}
          data-testid="worksheet-back"
          className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#4a6984] hover:text-[#1c2d3d]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Math Engine
        </button>
        <div className="inline-flex items-center bg-white rounded-full border border-[#4a6984]/15 p-1 shadow-sm">
          <button
            onClick={() => setMode("close_the_gap")}
            data-testid="worksheet-mode-gap"
            className={`px-4 py-1.5 rounded-full text-xs font-extrabold transition ${
              mode === "close_the_gap"
                ? "bg-[#1c2d3d] text-white"
                : "text-[#4a6984] hover:text-[#1c2d3d]"
            }`}
          >
            Close the Gap
          </button>
          <button
            onClick={() => setMode("custom_build")}
            data-testid="worksheet-mode-custom"
            className={`px-4 py-1.5 rounded-full text-xs font-extrabold transition ${
              mode === "custom_build"
                ? "bg-[#1c2d3d] text-white"
                : "text-[#4a6984] hover:text-[#1c2d3d]"
            }`}
          >
            Custom Build
          </button>
        </div>
      </div>

      {/* Recommendation banner (Close-the-Gap only) */}
      {mode === "close_the_gap" && (
        <div
          className="ae-card p-5 mb-6 bg-[#96ded1]/15 border-[#96ded1]/50"
          data-testid="worksheet-recommendation-banner"
        >
          {recLoading ? (
            <div className="inline-flex items-center gap-2 text-sm text-[#4a6984]">
              <Loader2 className="h-4 w-4 animate-spin" /> Looking at what you taught recently…
            </div>
          ) : recommendation?.available ? (
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[#1c2d3d] mb-1.5">
                <Sparkles className="h-3 w-3" /> Recommended
              </div>
              <p className="text-sm text-[#1c2d3d]">{recommendation.blurb}</p>
              <p className="text-xs text-[#4a6984]/70 mt-1.5 font-medium">
                The form below is pre-filled — change anything you like, then generate.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[#4a6984]">
              {recommendation?.message || "Switch to Custom Build to set it up yourself."}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FORM */}
        <div className="ae-card p-6 lg:col-span-2">
          <h3 className="font-black text-xl text-[#1c2d3d] mb-5 tracking-tight">
            {mode === "close_the_gap" ? "Adjust the worksheet" : "Build your worksheet"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Section label="Grade">
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger data-testid="worksheet-grade" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRADES.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
            <Section label="Strand">
              <Select value={strand} onValueChange={setStrand}>
                <SelectTrigger data-testid="worksheet-strand" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {strands.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
          </div>

          <Section
            label={`Expectations · ${selectedCodes.length} selected`}
            hint="Tick the Ontario expectations the worksheet should target."
            testid="worksheet-expectations-section"
          >
            <div className="rounded-lg border border-[#4a6984]/15 bg-white max-h-72 overflow-y-auto p-2">
              {expectationsLoading ? (
                <div className="text-sm text-[#4a6984]/70 p-3">Loading expectations…</div>
              ) : expectations.length === 0 ? (
                <div className="text-sm text-[#4a6984]/70 p-3">
                  No expectations found for {grade} → {strand}.
                </div>
              ) : (
                expectations.map((e) => {
                  const on = selectedCodes.includes(e.code);
                  return (
                    <label
                      key={e.code}
                      className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition ${
                        on ? "bg-[#96ded1]/15" : "hover:bg-[#f4f5f7]"
                      }`}
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={() => toggleCode(e.code)}
                        data-testid={`worksheet-exp-${e.code}`}
                      />
                      <div className="text-sm">
                        <div className="font-extrabold text-[#1c2d3d]">
                          {e.code} <span className="text-[#4a6984]/70 font-semibold">· {e.topic}</span>
                        </div>
                        <div className="text-[#4a6984]/85">{e.description}</div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </Section>

          <Section label="Question types" hint="Pick one or several — Atomic Edu mixes them for you.">
            <div className="flex flex-wrap gap-2">
              {qTypeCatalogue.map((qt) => (
                <Pill
                  key={qt.key}
                  active={questionTypes.includes(qt.key)}
                  onClick={() => toggleQType(qt.key)}
                  testid={`worksheet-qtype-${qt.key}`}
                >
                  {qt.label}
                </Pill>
              ))}
            </div>
          </Section>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Section label="Difficulty">
              <Select value={difficulty} onValueChange={setDifficulty} disabled={generateTrio}>
                <SelectTrigger data-testid="worksheet-difficulty" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
            <Section label="How many questions">
              <Select
                value={String(questionCount)}
                onValueChange={(v) => setQuestionCount(parseInt(v, 10))}
              >
                <SelectTrigger data-testid="worksheet-count" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_COUNTS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} questions
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
            <Section label="Layout">
              <Select value={layout} onValueChange={setLayout}>
                <SelectTrigger data-testid="worksheet-layout" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAYOUT_OPTIONS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
          </div>

          <Section label="Title (optional)" hint="Leave blank to let Atomic Edu write one.">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Friday fractions check"
              data-testid="worksheet-title"
              className="bg-white"
            />
          </Section>

          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7]/50 cursor-pointer mb-5"
            data-testid="worksheet-trio"
          >
            <Checkbox
              checked={generateTrio}
              onCheckedChange={(v) => setGenerateTrio(!!v)}
            />
            <div className="text-sm">
              <div className="font-extrabold text-[#1c2d3d]">
                Generate all three difficulty levels at once
              </div>
              <div className="text-[#4a6984]/85">
                Intervention · On Grade · Extension — perfect for differentiated stations or guided groups.
              </div>
            </div>
          </label>

          <Button
            onClick={submit}
            disabled={submitting || anyRunning || selectedCodes.length === 0}
            data-testid="worksheet-generate"
            className="w-full h-12 bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 rounded-lg font-extrabold text-sm"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2" />
                {generateTrio ? "Generate 3 worksheets" : "Generate worksheet"}
              </>
            )}
          </Button>
        </div>

        {/* RESULTS */}
        <div className="ae-card p-6">
          <h3 className="font-black text-lg text-[#1c2d3d] mb-4 tracking-tight">Your worksheets</h3>
          {jobs.length === 0 ? (
            <p className="text-sm text-[#4a6984]/70">
              Your worksheets will land here once you generate. Two documents per worksheet — the
              student version and the teacher answer key.
            </p>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => (
                <WorksheetJobCard key={j.job_id} job={j} />
              ))}
            </div>
          )}
          {anyDone && (
            <p className="text-xs text-[#4a6984]/65 mt-4 font-medium">
              Worksheets are saved in your <strong>My Library</strong> automatically.
            </p>
          )}
        </div>
      </div>
      {/* Phase 88 — Pro-tier post-generation Planner popup. Silently
          skips when the user isn't a Pro Planner subscriber. */}
      {justGenerated && (
        <PostGenScheduleDialog
          key={justGenerated.id}
          activity={justGenerated}
          onDismiss={() => setJustGenerated(null)}
        />
      )}
    </div>
  );
}

const DIFFICULTY_LABELS = {
  intervention: "Intervention",
  on_grade: "On Grade",
  extension: "Extension",
};

const WorksheetJobCard = ({ job }) => {
  const a = job.activity;
  const diffLabel = DIFFICULTY_LABELS[job.difficulty] || job.difficulty;
  const downloadHref = (fmt) =>
    a ? `${process.env.REACT_APP_BACKEND_URL}/api/activities/${a.id}/export?format=${fmt}` : "#";

  return (
    <div
      className="rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7]/50 p-4"
      data-testid={`worksheet-job-${job.difficulty}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#1c2d3d] bg-[#96ded1]/35 px-2 py-0.5 rounded-full">
          {diffLabel}
        </span>
        {job.status === "running" && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-[#4a6984] font-semibold"
            data-testid={`worksheet-job-status-${job.difficulty}`}
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            {/* Feb 10 2026 — surface the backend waypoint verbatim if we
                have one, otherwise fall back to the generic verb. */}
            <span data-testid={`worksheet-job-progress-${job.difficulty}`}>
              {job.progress && job.progress.trim() ? `${job.progress}…` : "Building…"}
            </span>
          </span>
        )}
        {job.status === "error" && (
          <span className="text-xs text-red-600 font-semibold">Failed</span>
        )}
      </div>
      {job.status === "done" && a && (
        <>
          <p className="font-extrabold text-sm text-[#1c2d3d] mb-3 leading-snug">
            {a.activity_name}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={downloadHref("worksheet")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
              data-testid={`worksheet-dl-student-${job.difficulty}`}
            >
              <Download className="h-3 w-3" /> Student PDF
            </a>
            <a
              href={downloadHref("worksheet_key")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
              data-testid={`worksheet-dl-key-${job.difficulty}`}
            >
              <ClipboardCheck className="h-3 w-3" /> Answer Key
            </a>
            <a
              href={downloadHref("worksheet_combined")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1c2d3d] text-white text-xs font-extrabold hover:bg-[#1c2d3d]/90"
              data-testid={`worksheet-dl-combo-${job.difficulty}`}
            >
              <FileText className="h-3 w-3" /> Both (combined)
            </a>
          </div>
        </>
      )}
      {job.status === "error" && (
        <p className="text-xs text-red-600/85 mt-2">{job.error || "Something went wrong."}</p>
      )}
    </div>
  );
};
